import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { FadeUp } from "./Motion";
import { CheckCircle2, ShieldCheck, TrendingUp } from "lucide-react";

const bars = [
  { label: "Financial Health", value: 425, max: 500, note: "Stable revenue · Healthy DSCR · GST-compliant 36 mo" },
  { label: "Operational Health", value: 405, max: 500, note: "Diversified buyers · Verified trading partners" },
];

const insights = [
  {
    icon: TrendingUp,
    title: "Revenue trajectory is durable",
    body: "18-month revenue trend shows +14% YoY with low variance across seasons.",
  },
  {
    icon: ShieldCheck,
    title: "Distributor payments on time",
    body: "Consistent supplier payment behaviour across 9 verified relationships.",
  },
  {
    icon: CheckCircle2,
    title: "Digital-native footprint",
    body: "UPI + marketplace mix indicates strong resilience to offline shocks.",
  },
];

const CountUp = ({ target, duration = 1600, inView }) => {
  const reduced = useReducedMotion();
  const [val, setVal] = useState(reduced ? target : 0);
  useEffect(() => {
    if (reduced || !inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration, reduced]);
  return <>{val}</>;
};

export const SampleReport = () => {
  const cardRef = useRef(null);
  const inView = useInView(cardRef, { once: true, margin: "-15% 0px -15% 0px" });

  return (
    <section id="report" className="relative py-24 md:py-36 border-t border-ink/10 bg-navy text-paper overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(250,250,250,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(250,250,250,0.5) 1px, transparent 1px)",
          backgroundSize: "72px 72px",
        }}
      />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-10">
        <FadeUp>
          <div className="flex items-center gap-4 md:gap-6 mb-16 flex-wrap">
            <span className="font-mono-cap text-paper/60">§ 04 — Sample Report</span>
            <span className="flex-1 min-w-8 h-px bg-paper/20" />
            <span className="font-mono-cap text-paper/50">Concept preview</span>
          </div>
        </FadeUp>

        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <FadeUp>
              <h2 className="font-display text-5xl md:text-7xl text-paper leading-[0.98]">
                What a Health Report <br />
                <span className="italic text-teal">could</span> look like.
              </h2>
            </FadeUp>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <FadeUp delay={0.15}>
              <p className="font-sans text-paper/70 leading-relaxed">
                V1 assesses two verified data pillars — Financial Health
                (0–500) and Operational Health (0–500) — for an overall
                score out of 1000. Two more pillars land in V2.
              </p>
            </FadeUp>
          </div>
        </div>

        {/* Report mockup */}
        <FadeUp delay={0.15} className="mt-16 md:mt-20">
          <div ref={cardRef} className="bg-paper text-ink border border-paper/20 relative">
            <div className="grid grid-cols-12 gap-0">
              {/* Left — score */}
              <div className="col-span-12 md:col-span-5 p-8 md:p-12 border-b md:border-b-0 md:border-r border-ink/10">
                <div className="flex items-center justify-between">
                  <span className="font-mono-cap text-ink/60">Business Health Score</span>
                  <span className="font-mono-cap text-ink/60">MHS</span>
                </div>
                <div className="mt-6 flex items-end gap-4">
                  <motion.div
                    className="font-display text-[6.5rem] md:text-[8.5rem] leading-none text-ink tabular-nums"
                    initial={{ opacity: 0, y: 16 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    data-testid="sample-score"
                  >
                    <CountUp target={830} inView={inView} />
                  </motion.div>
                  <motion.div
                    className="pb-4"
                    initial={{ opacity: 0 }}
                    animate={inView ? { opacity: 1 } : {}}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <div className="font-mono-cap text-teal">High Potential</div>
                    <div className="font-sans text-sm text-ink/60 mt-1">out of 1000</div>
                  </motion.div>
                </div>

                <div className="mt-8 border-t border-ink/10 pt-6">
                  <div className="font-mono-cap text-ink/60 mb-2">Business</div>
                  <div className="font-display text-2xl text-ink">Kesari Trading Co.</div>
                  <div className="font-sans text-sm text-ink/60 mt-1">
                    Wholesale · 11 yrs · Rajasthan
                  </div>
                </div>

                <div className="mt-8 grid grid-cols-3 gap-4 border-t border-ink/10 pt-6">
                  {[
                    ["Vintage", "11 yrs"],
                    ["Verified Rels.", "12"],
                    ["Data Coverage", "94%"],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="font-mono-cap text-ink/50">{k}</div>
                      <div className="font-display text-xl text-ink mt-1">{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right — pillars */}
              <div className="col-span-12 md:col-span-7 p-8 md:p-12">
                <div className="font-mono-cap text-ink/60 mb-6">Two-Pillar Breakdown (V1)</div>
                <div className="space-y-6">
                  {bars.map((b, i) => (
                    <div key={b.label}>
                      <div className="flex items-baseline justify-between">
                        <div className="font-display text-xl text-ink">{b.label}</div>
                        <div className="font-mono-cap text-ink tabular-nums">
                          <CountUp target={b.value} duration={1400 + i * 120} inView={inView} />
                        </div>
                      </div>
                      <div className="score-bar mt-2">
                        <motion.span
                          initial={{ width: 0 }}
                          animate={inView ? { width: `${Math.round((b.value / (b.max || 100)) * 100)}%` } : {}}
                          transition={{
                            duration: 1.2,
                            ease: [0.22, 1, 0.36, 1],
                            delay: 0.15 + i * 0.12,
                          }}
                          style={{ position: "absolute", inset: "0 auto 0 0", height: "100%", background: "#C2984A" }}
                        />
                      </div>
                      <div className="font-mono-cap text-ink/50 mt-2">{b.note}</div>
                    </div>
                  ))}
                </div>

                <div className="mt-10 border-t border-ink/10 pt-6">
                  <div className="font-mono-cap text-ink/60 mb-4">Notable Insights</div>
                  <div className="space-y-4">
                    {insights.map((it) => {
                      const Icon = it.icon;
                      return (
                        <div key={it.title} className="flex gap-4">
                          <div className="w-9 h-9 flex items-center justify-center bg-ink text-paper shrink-0">
                            <Icon size={16} strokeWidth={1.5} />
                          </div>
                          <div>
                            <div className="font-display text-lg text-ink leading-tight">
                              {it.title}
                            </div>
                            <div className="font-sans text-sm text-ink/70 mt-1">{it.body}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            <div className="border-t border-ink/10 px-8 md:px-12 py-4 flex items-center justify-between">
              <div className="font-mono-cap text-ink/50">
                MSME Health Score · Draft layout
              </div>
              <div className="font-mono-cap text-ink/50">Concept preview</div>
            </div>
          </div>
          {/* Subtle fine-print disclaimer beneath the card */}
          <p
            data-testid="sample-disclaimer"
            className="mt-4 text-xs text-paper/50 font-sans"
          >
            Illustrative sample based on synthetic data. Not a live product or generated report.
          </p>
        </FadeUp>
      </div>
    </section>
  );
};
